import { Button } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateKpiSidePanel, Notification } from '../remote-modules';
import { getSharedData, openCreateKpiModal, openViewKpiModal } from '../utils';
import {
  KPI_ID_PARAM,
  KPI_LOCATION_PARAM,
  KPI_LOCATION_VALUES,
  KPI_MODAL_PARAM,
  type KPILocationValue,
} from '../constants';

type KpiDTO = {
  id: number;
  teamResponsibleIds: number[];
};

type CreateKpiSidePanelWrapperProps = {
  open: boolean;
  teamId: string | null;
  location: KPILocationValue | null;
  onClose: () => void;
};

export const CreateKpiSidePanelWrapper: React.FC<CreateKpiSidePanelWrapperProps> = ({
  open,
  teamId,
  location,
  onClose,
}) => {
  const { t } = useTranslation();

  const [showNotification, setShowNotification] = useState(false);
  const [kpiId, setKpiId] = useState<string | null>(null);

  const sharedData = useMemo(() => getSharedData(location), [location]);

  const externalData = useMemo(
    () => ({
      teamId,
      onClose,
      ...sharedData,
    }),
    [teamId, onClose, sharedData],
  );

  const handleOpenCreateAnother = useCallback(() => {
    setShowNotification(false);

    openCreateKpiModal(KPI_LOCATION_VALUES.TOAST_CREATE_ANOTHER);
  }, []);

  const handleOpenViewKpi = useCallback(() => {
    setShowNotification(false);

    openViewKpiModal(kpiId, KPI_LOCATION_VALUES.TOAST_VIEW_KPI);
  }, [kpiId]);

  const handleSuccess = useCallback(
    (kpi: KpiDTO) => {
      setShowNotification(true);
      setKpiId(kpi.id.toString());

      /** when using teams page and the kpi created is for the current team, redirect to the kpis tab */
      const isCurrentTeamKpi = kpi.teamResponsibleIds?.includes(Number(teamId));
      const hasKpiTab = document.getElementById('team-view-kpis-tab');

      /** closes the create kpi modal, does a page reload and navigates to the kpis tab */
      if (isCurrentTeamKpi && !hasKpiTab) {
        const url = new URL(window.location.href);
        url.searchParams.set('selectedTab', 'kpis');
        url.searchParams.delete(KPI_MODAL_PARAM);
        url.searchParams.delete(KPI_ID_PARAM);
        url.searchParams.delete(KPI_LOCATION_PARAM);
        window.location.assign(url.toString());
      }
    },
    [location, teamId],
  );

  const actions = (
    <>
      <Button variant="text" onClick={handleOpenCreateAnother}>
        {t('Create another')}
      </Button>
      {kpiId && (
        <Button variant="text" onClick={handleOpenViewKpi}>
          {t('View KPI')}
        </Button>
      )}
    </>
  );

  return (
    <>
      {open && <CreateKpiSidePanel {...externalData} onSuccess={handleSuccess} />}
      {showNotification && (
        <Notification
          open={showNotification}
          message={t('Your KPI has been created successfully')}
          type="success"
          actions={actions}
          onClose={() => setShowNotification(false)}
        />
      )}
    </>
  );
};
